import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useRestaurant } from '@/hooks/useRestaurant';
import { OnboardingFlow } from '@/components/OnboardingFlow';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiresRestaurant?: boolean;
  requiresOnboarding?: boolean;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiresRestaurant = false,
  requiresOnboarding = true
}) => {
  const { user, loading: authLoading, isStaff, staffSession } = useAuth();
  const { restaurant, loading: restaurantLoading, refetch, error } = useRestaurant();

  // Debug logging
  React.useEffect(() => {
    if (!authLoading && !restaurantLoading) {
      console.log('[ProtectedRoute] State:', {
        user: user?.id,
        isStaff,
        restaurant: restaurant?.id,
        restaurantLoading,
        error: error?.message,
        requiresOnboarding
      });
    }
  }, [user, isStaff, restaurant, restaurantLoading, error, requiresOnboarding, authLoading]);

  // Show loading spinner while auth is being determined
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Loading...</span>
        </div>
      </div>
    );
  }

  // Check if user is authenticated (either regular user or staff)
  const isAuthenticated = user || isStaff;

  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // For staff users, they don't need a restaurant check since they're linked to one
  if (isStaff && staffSession) {
    return <>{children}</>;
  }

  // For regular users, wait for restaurant loading to complete
  // This is critical - don't show onboarding while still fetching restaurant data
  if (restaurantLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">Loading restaurant...</span>
        </div>
      </div>
    );
  }

  // Check restaurant requirement
  if (requiresRestaurant && !restaurant) {
    return <Navigate to="/dashboard/business-profile" replace />;
  }

  // Show onboarding flow if user doesn't have a restaurant and route requires onboarding
  if (!restaurant && requiresOnboarding) {
    console.warn('[ProtectedRoute] Showing onboarding - no restaurant found for user:', user?.id);
    return <OnboardingFlow onComplete={refetch} />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
